import {
  arrayItemSchema,
  CASE_TYPE_SCHEMA,
  emptyValueForSchema,
  getSchemaAt,
  isArraySchema,
  isLeafSchema,
  listObjectFields,
  type TypeSchema,
} from "../../../case-spec";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Field, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import {
  DEVICE_SHELL_KEYS,
  labelForKey,
  PACKAGE_ROOT_KEYS,
} from "@/lib/phone-json-labels";
import { cn } from "@/lib/utils";
import { OpaqueJsonField, PrimitiveField } from "./field-widgets";
import { renderSpecialPanel } from "./panels";
import {
  appendAtPath,
  childPath,
  getAtPath,
  moveAtIndex,
  removeAtIndex,
  setAtPath,
} from "./path-utils";
import { rowTitle } from "./row-title";
import {
  ArraySortPathProvider,
  ItemOrderControls,
} from "./array-sort-ui";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function schemaAt(path: string): TypeSchema | null {
  if (!path) return CASE_TYPE_SCHEMA;
  return getSchemaAt(CASE_TYPE_SCHEMA, path);
}

export function JsonFormTree({
  root,
  sectionPath,
  disabled,
  onChangeRoot,
}: {
  root: unknown;
  /** Empty string = package root fields only. */
  sectionPath: string;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
}) {
  const value = sectionPath ? getAtPath(root, sectionPath) : root;
  const schema = schemaAt(sectionPath);

  if (sectionPath === "") {
    return (
      <PackageRootForm
        root={root}
        disabled={disabled}
        onChangeRoot={onChangeRoot}
      />
    );
  }

  if (sectionPath === "device") {
    return (
      <DeviceShellForm
        root={root}
        disabled={disabled}
        onChangeRoot={onChangeRoot}
      />
    );
  }

  const special = renderSpecialPanel(sectionPath, {
    root,
    disabled,
    onChangeRoot,
  });
  if (special) {
    // Specialized panels can create missing bags via "添加"
    if (value === undefined && schema && !disabled) {
      // still show panel; most panels treat missing as empty array
      return special;
    }
    return special;
  }

  if (value === undefined) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">此区块尚无数据。</p>
        {schema && !disabled ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              onChangeRoot(
                setAtPath(root, sectionPath, emptyValueForSchema(schema)),
              );
            }}
          >
            创建空区块
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <NodeEditor
      root={root}
      path={sectionPath}
      value={value}
      schema={schema}
      disabled={disabled}
      onChangeRoot={onChangeRoot}
      depth={0}
    />
  );
}

function PackageRootForm({
  root,
  disabled,
  onChangeRoot,
}: {
  root: unknown;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
}) {
  const record = isRecord(root) ? root : {};
  return (
    <div className="flex flex-col gap-3">
      {PACKAGE_ROOT_KEYS.map((key) => {
        const path = key;
        const childSchema = getSchemaAt(CASE_TYPE_SCHEMA, path);
        if (key === "phone_runtime_version" && record[key] === undefined) {
          return (
            <div key={key} className="flex items-end gap-2">
              <div className="min-w-0 flex-1">
                <PrimitiveField
                  fieldKey={key}
                  path={path}
                  value=""
                  schema={childSchema}
                  disabled
                  onChange={() => {}}
                />
              </div>
              {!disabled ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onChangeRoot(setAtPath(root, path, ""))}
                >
                  添加
                </Button>
              ) : null}
            </div>
          );
        }
        return (
          <PrimitiveField
            key={key}
            fieldKey={key}
            path={path}
            value={record[key]}
            schema={childSchema}
            disabled={disabled || key === "id"}
            onChange={(next) => onChangeRoot(setAtPath(root, path, next))}
          />
        );
      })}
      <p className="text-xs text-muted-foreground">
        案件 ID 由系统固定，请勿在此改写其它语言包身份。大块内容请用左侧导航进入对应区块。
      </p>
    </div>
  );
}

function DeviceShellForm({
  root,
  disabled,
  onChangeRoot,
}: {
  root: unknown;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
}) {
  const deviceSchema = schemaAt("device");
  const fieldByKey = new Map(
    deviceSchema &&
      typeof deviceSchema === "object" &&
      !Array.isArray(deviceSchema)
      ? listObjectFields(deviceSchema).map((field) => [field.key, field] as const)
      : [],
  );

  return (
    <div className="flex flex-col gap-3">
      {DEVICE_SHELL_KEYS.map((key) => {
        const path = childPath("device", key);
        const value = getAtPath(root, path);
        const field = fieldByKey.get(key);
        const schema = field?.schema ?? schemaAt(path);

        if (value === undefined && field?.optional) {
          return (
            <OptionalMissingField
              key={key}
              label={labelForKey(key)}
              disabled={disabled}
              onAdd={() => {
                const empty = schema ? emptyValueForSchema(schema) : "";
                onChangeRoot(setAtPath(root, path, empty));
              }}
            />
          );
        }

        return (
          <NodeEditor
            key={key}
            root={root}
            path={path}
            value={value}
            schema={schema}
            disabled={disabled}
            onChangeRoot={onChangeRoot}
            depth={0}
            forceKey={key}
          />
        );
      })}
      <p className="text-xs text-muted-foreground">
        设备时间是冻结的系统时钟（不走时），格式 YYYY-MM-DDTHH:mm:ss。未设置时运行时用回退时间。
      </p>
    </div>
  );
}

function NodeEditor({
  root,
  path,
  value,
  schema,
  disabled,
  onChangeRoot,
  depth,
  forceKey,
}: {
  root: unknown;
  path: string;
  value: unknown;
  schema: TypeSchema | null;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
  depth: number;
  forceKey?: string;
}) {
  const leafKey = forceKey ?? path.split(".").pop()?.replace(/\[\d+\]$/, "") ?? path;

  if (schema && isLeafSchema(schema)) {
    if (schema === "object") {
      return (
        <OpaqueJsonField
          fieldKey={leafKey}
          path={path}
          value={value}
          disabled={disabled}
          onChange={(next) => onChangeRoot(setAtPath(root, path, next))}
        />
      );
    }
    return (
      <PrimitiveField
        fieldKey={leafKey}
        path={path}
        value={value}
        schema={schema}
        disabled={disabled}
        onChange={(next) => onChangeRoot(setAtPath(root, path, next))}
      />
    );
  }

  if (schema && isArraySchema(schema)) {
    return (
      <ArrayEditor
        root={root}
        path={path}
        value={Array.isArray(value) ? value : []}
        itemSchema={arrayItemSchema(schema)}
        disabled={disabled}
        onChangeRoot={onChangeRoot}
        depth={depth}
      />
    );
  }

  // No schema or object schema
  if (Array.isArray(value)) {
    const itemSchema = schema && isArraySchema(schema) ? arrayItemSchema(schema) : null;
    return (
      <ArrayEditor
        root={root}
        path={path}
        value={value}
        itemSchema={itemSchema}
        disabled={disabled}
        onChangeRoot={onChangeRoot}
        depth={depth}
      />
    );
  }

  if (isRecord(value) || (schema && typeof schema === "object" && !Array.isArray(schema))) {
    return (
      <ObjectEditor
        root={root}
        path={path}
        value={isRecord(value) ? value : {}}
        schema={schema}
        disabled={disabled}
        onChangeRoot={onChangeRoot}
        depth={depth}
        showTitle={depth > 0}
        titleKey={leafKey}
      />
    );
  }

  // Fallback primitive without schema
  return (
    <PrimitiveField
      fieldKey={leafKey}
      path={path}
      value={value}
      schema={null}
      disabled={disabled}
      onChange={(next) => onChangeRoot(setAtPath(root, path, next))}
    />
  );
}

function ObjectEditor({
  root,
  path,
  value,
  schema,
  disabled,
  onChangeRoot,
  depth,
  showTitle,
  titleKey,
}: {
  root: unknown;
  path: string;
  value: Record<string, unknown>;
  schema: TypeSchema | null;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
  depth: number;
  showTitle: boolean;
  titleKey: string;
}) {
  const fields =
    schema && typeof schema === "object" && !Array.isArray(schema)
      ? listObjectFields(schema)
      : Object.keys(value).map((key) => ({
          key,
          optional: true,
          schema: null as TypeSchema | null,
        }));

  // Include extra keys present in data but not in schema
  const known = new Set(fields.map((f) => f.key));
  const extraKeys = Object.keys(value).filter((k) => !known.has(k));

  const body = (
    <div className={cn("flex flex-col gap-3", showTitle && "border-l border-border/60 pl-3")}>
      {fields.map((field) => {
        const child = childPath(path, field.key);
        const childValue = value[field.key];
        const childSchema =
          field.schema ??
          (schema ? getSchemaAt(schema, field.key) : schemaAt(child));

        if (childValue === undefined) {
          if (field.optional) {
            return (
              <OptionalMissingField
                key={field.key}
                label={labelForKey(field.key)}
                disabled={disabled}
                onAdd={() => {
                  const empty = field.schema
                    ? emptyValueForSchema(field.schema)
                    : "";
                  onChangeRoot(setAtPath(root, child, empty));
                }}
              />
            );
          }
        }

        return (
          <NodeEditor
            key={field.key}
            root={root}
            path={child}
            value={childValue}
            schema={childSchema}
            disabled={disabled}
            onChangeRoot={onChangeRoot}
            depth={depth + 1}
            forceKey={field.key}
          />
        );
      })}
      {extraKeys.map((key) => {
        const child = childPath(path, key);
        return (
          <NodeEditor
            key={key}
            root={root}
            path={child}
            value={value[key]}
            schema={schemaAt(child)}
            disabled={disabled}
            onChangeRoot={onChangeRoot}
            depth={depth + 1}
            forceKey={key}
          />
        );
      })}
    </div>
  );

  if (!showTitle) return body;

  return (
    <Collapsible defaultOpen={depth < 2}>
      <CollapsibleTrigger className="mb-2 flex w-full items-center gap-1 rounded-sm px-1 py-0.5 text-left text-sm font-medium hover:bg-muted">
        {labelForKey(titleKey)}
      </CollapsibleTrigger>
      <CollapsibleContent>{body}</CollapsibleContent>
    </Collapsible>
  );
}

function OptionalMissingField({
  label,
  disabled,
  onAdd,
}: {
  label: string;
  disabled?: boolean;
  onAdd: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-dashed border-border px-2 py-1.5">
      <span className="text-xs text-muted-foreground">{label}（未设置）</span>
      <Button
        type="button"
        size="xs"
        variant="ghost"
        disabled={disabled}
        onClick={onAdd}
      >
        添加
      </Button>
    </div>
  );
}

function ArrayEditor({
  root,
  path,
  value,
  itemSchema,
  disabled,
  onChangeRoot,
  depth,
}: {
  root: unknown;
  path: string;
  value: unknown[];
  itemSchema: TypeSchema | null;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
  depth: number;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(
    value.length > 0 ? 0 : null,
  );
  const leafKey = path.split(".").pop()?.replace(/\[\d+\]$/, "") ?? path;

  // string arrays: simple multi-line editor
  if (
    itemSchema &&
    isLeafSchema(itemSchema) &&
    itemSchema !== "object" &&
    value.every((v) => typeof v === "string" || v == null)
  ) {
    return (
      <Field>
        <FieldLabel>{labelForKey(leafKey)}</FieldLabel>
        <Textarea
          value={value.map((v) => (v == null ? "" : String(v))).join("\n")}
          disabled={disabled}
          rows={Math.min(8, Math.max(3, value.length + 1))}
          className="min-h-16 resize-y text-sm"
          onChange={(e) => {
            const lines = e.target.value.split("\n");
            onChangeRoot(setAtPath(root, path, lines));
          }}
        />
        <p className="text-[0.65rem] text-muted-foreground">每行一项</p>
      </Field>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">
          {labelForKey(leafKey)}
          <span className="ml-1.5 text-xs font-normal text-muted-foreground">
            {value.length} 条
          </span>
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => {
            const empty = itemSchema ? emptyValueForSchema(itemSchema) : {};
            const next = appendAtPath(root, path, empty);
            onChangeRoot(next);
            setOpenIndex(value.length);
          }}
        >
          添加
        </Button>
      </div>

      {value.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
          暂无条目，点击「添加」新建。
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {value.map((item, index) => {
            const itemPath = childPath(path, index);
            const open = openIndex === index;
            return (
              <li
                key={itemPath}
                className="rounded-md border border-border bg-card"
              >
                <div className="flex items-center gap-1 border-b border-border/60 px-2 py-1">
                  <button
                    type="button"
                    className="min-w-0 flex-1 truncate rounded-sm px-1 py-0.5 text-left text-xs font-medium hover:bg-muted"
                    onClick={() => setOpenIndex(open ? null : index)}
                  >
                    <span className="mr-1 text-muted-foreground">
                      {open ? "▾" : "▸"}
                    </span>
                    {rowTitle(item, index, path)}
                  </button>
                  <ItemOrderControls
                    disabled={disabled}
                    index={index}
                    total={value.length}
                    arrayPath={path}
                    onMove={(delta) =>
                      onChangeRoot(moveAtIndex(root, path, index, delta))
                    }
                  />
                  <Button
                    type="button"
                    size="icon-xs"
                    variant="ghost"
                    disabled={disabled}
                    title="删除"
                    className="text-destructive hover:text-destructive"
                    onClick={() => {
                      if (
                        !window.confirm(
                          `删除「${rowTitle(item, index, path)}」？`,
                        )
                      ) {
                        return;
                      }
                      onChangeRoot(removeAtIndex(root, path, index));
                      setOpenIndex(null);
                    }}
                  >
                    ✕
                  </Button>
                </div>
                {open ? (
                  <ArraySortPathProvider arrayPath={path}>
                    <div className="p-3">
                      <NodeEditor
                        root={root}
                        path={itemPath}
                        value={item}
                        schema={itemSchema}
                        disabled={disabled}
                        onChangeRoot={onChangeRoot}
                        depth={depth + 1}
                      />
                    </div>
                  </ArraySortPathProvider>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
